import { type Def, FLAG_DEFS, TWEAK_DEFS, hasReloadDef, resetAll } from '@/lib/feature-flags';
import { useEffect, useMemo, useState } from 'react';
import { DevPanelRow } from './dev-panel-row';

interface DevPanelSheetProps {
  onClose: () => void;
}

type TabKey = 'flags' | 'tweaks';

function matches(def: Def, query: string): boolean {
  if (query === '') return true;
  const haystack = `${def.label} ${def.description} ${def.key}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

/**
 * The dev panel: a compact floating card anchored above the launcher (bottom-right
 * on desktop, a bottom strip on mobile) with Flags / Tweaks tabs and a search box.
 *
 * It is a non-modal `<dialog>` — no backdrop, no page dimming — so the page stays
 * fully interactive while you toggle. The card grows with its content up to half
 * the viewport height, after which the list scrolls; its width never changes. It
 * closes only via the × button or Escape, so it persists while you inspect the page.
 */
export function DevPanelSheet({ onClose }: DevPanelSheetProps) {
  const [tab, setTab] = useState<TabKey>('tweaks');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const source = tab === 'flags' ? FLAG_DEFS : TWEAK_DEFS;
  const visible = useMemo(() => source.filter((def) => matches(def, query)), [source, query]);

  function handleReset(): void {
    resetAll();
    if (hasReloadDef()) {
      try {
        globalThis.location?.reload();
      } catch {
        // ignore — nothing to reload outside the browser
      }
    }
  }

  return (
    <dialog open className="devp-sheet" aria-label="Dev panel">
      <header className="devp-sheet__head">
        <span className="devp-sheet__title">Dev panel</span>
        <button type="button" className="devp-sheet__close" aria-label="Close" onClick={onClose}>
          <span className="material-symbols-rounded">close</span>
        </button>
      </header>

      <div className="devp-tabs" role="tablist" aria-label="Dev panel sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tweaks'}
          className={`devp-tab${tab === 'tweaks' ? ' is-active' : ''}`}
          onClick={() => setTab('tweaks')}
        >
          Tweaks <span className="devp-tab__count">{TWEAK_DEFS.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'flags'}
          className={`devp-tab${tab === 'flags' ? ' is-active' : ''}`}
          onClick={() => setTab('flags')}
        >
          Flags <span className="devp-tab__count">{FLAG_DEFS.length}</span>
        </button>
      </div>

      <div className="devp-search">
        <span className="material-symbols-rounded devp-search__icon">search</span>
        <input
          type="text"
          className="devp-search__input"
          placeholder="Filter…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="devp-list">
        {visible.length === 0 ? (
          <p className="devp-empty">
            {tab === 'tweaks' && TWEAK_DEFS.length === 0
              ? 'No tweaks registered yet. Add an entry to DEFS in lib/feature-flags.ts.'
              : 'No matches.'}
          </p>
        ) : (
          visible.map((def) => <DevPanelRow key={def.key} def={def} />)
        )}
      </div>

      <footer className="devp-sheet__foot">
        <button type="button" className="devp-reset" onClick={handleReset}>
          Reset all to defaults
        </button>
      </footer>
    </dialog>
  );
}
